'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import NotificationCenter from '@/components/NotificationCenter';

interface HeaderProps {
  darkMode: boolean;
  onToggleDark: () => void;
}

export default function Header({ darkMode, onToggleDark }: HeaderProps) {
  const pathname = usePathname();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    setMounted(true);
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '/homepage', label: 'Home' },
    { href: '/templates', label: 'Templates' },
    { href: '/wishlist', label: 'Wishlist' },
    { href: '/faq', label: 'FAQ' },
    { href: '/community', label: 'Community' }
  ];

  // Only add Support link after mount to avoid hydration mismatch
  const effectiveUser = mounted ? user : null;

  // Add Support link for logged-in users
  const userNavLinks = effectiveUser ? [
    ...navLinks,
    { href: '/support', label: 'Support' }
  ] : navLinks;


  const isActive = (href: string) => pathname === href;

  // Use stable values for SSR, actual values after mount
  const isScrolled = mounted && scrolled;
  const isDark = mounted && darkMode;

  return (
    <header
      suppressHydrationWarning
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ?
      'shadow-md backdrop-blur-xl' :
      ''}`
      }
      style={{
        background: isScrolled ?
        isDark ?
        'rgba(13,17,23,0.92)' :
        'rgba(248,246,241,0.92)' : 'transparent',
        borderBottom: isScrolled ? '1px solid var(--border)' : 'none'
      }}>

      <div className="max-w-8xl mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link href="/homepage" className="flex items-center gap-2">
          <AppLogo size={32} />
          <span
            className="font-display font-semibold text-lg"
            style={{ color: 'var(--text-primary)' }}>ZyForge


          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1">
          {userNavLinks.map((link) =>
          <Link
            key={link.href}
            href={link.href}
            className={`nav-link px-3 py-1.5 text-sm font-medium transition-colors ${
            isActive(link.href) ? 'active' : ''}`
            }
            style={{
              color: isActive(link.href) ? 'var(--accent)' : 'var(--text-secondary)'
            }}>

              {link.label}
            </Link>
          )}
          {effectiveUser && (
            <Link
              href="/admin"
              className={`nav-link px-3 py-1.5 text-sm font-medium transition-colors ${
              isActive('/admin') ? 'active' : ''}`
              }
              style={{
                color: isActive('/admin') ? 'var(--accent)' : 'var(--text-secondary)'
              }}>
              Admin
            </Link>
          )}
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-3">
          {/* Dark mode toggle */}
          <button
            onClick={onToggleDark}
            aria-label="Toggle dark mode"
            className="w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-110"
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              color: 'var(--text-secondary)'
            }}>

            <Icon name={darkMode ? 'SunIcon' : 'MoonIcon'} size={16} />
          </button>

          {/* User-specific features - only show when logged in */}
          {effectiveUser ? (
            <>
              {/* Notification Center */}
              <NotificationCenter />

              {/* Cart */}
              <Link
                href="/checkout"
                className="w-9 h-9 rounded-lg flex items-center justify-center relative transition-all duration-200 hover:scale-110"
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border)',
                  color: 'var(--text-secondary)'
                }}
                aria-label="Cart">
                <Icon name="ShoppingCartIcon" size={16} />
                <span
                  className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-white flex items-center justify-center"
                  style={{ background: 'var(--accent)', fontSize: '9px', fontWeight: 700 }}>
                  2
                </span>
              </Link>

              {/* Account */}
              <Link
                href="/account-dashboard"
                className="hidden md:flex btn-secondary text-sm py-2 px-4"
                style={{ fontSize: '13px' }}>
                <Icon name="UserIcon" size={14} />
                Account
              </Link>

              {/* Email History */}
              <Link
                href="/account/emails"
                className="hidden md:flex w-9 h-9 rounded-lg items-center justify-center transition-all duration-200 hover:scale-110"
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border)',
                  color: 'var(--text-secondary)'
                }}
                aria-label="Email History">
                <Icon name="EnvelopeIcon" size={16} />
              </Link>

              {/* Settings */}
              <Link
                href="/settings"
                className="hidden md:flex w-9 h-9 rounded-lg items-center justify-center transition-all duration-200 hover:scale-110"
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border)',
                  color: 'var(--text-secondary)'
                }}
                aria-label="Settings">
                <Icon name="Cog6ToothIcon" size={16} />
              </Link>
            </>
          ) : (
            /* Sign In - only show when NOT logged in */
            <Link href="/sign-up-login" className="btn-primary py-2 px-4" style={{ fontSize: '13px' }}>
              Sign In
            </Link>
          )}

          {/* Mobile menu button */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden w-9 h-9 rounded-lg flex items-center justify-center"
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              color: 'var(--text-secondary)'
            }}
            aria-label="Menu">

            <Icon name={menuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={18} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mounted && menuOpen &&
      <div
        className="md:hidden border-t"
        style={{
          background: 'var(--bg-secondary)',
          borderColor: 'var(--border)'
        }}>

          <div className="px-6 py-4 flex flex-col gap-1">
            {userNavLinks.map((link) =>
          <Link
            key={link.href}
            href={link.href}
            onClick={() => setMenuOpen(false)}
            className="px-3 py-2.5 rounded-lg text-sm font-medium transition-colors"
            style={{
              color: isActive(link.href) ? 'var(--accent)' : 'var(--text-secondary)',
              background: isActive(link.href) ? 'var(--accent-light)' : 'transparent'
            }}>

                {link.label}
              </Link>
          )}
            {effectiveUser && (
              <>
                <Link
                  href="/admin"
                  onClick={() => setMenuOpen(false)}
                  className="px-3 py-2.5 rounded-lg text-sm font-medium transition-colors"
                  style={{
                    color: isActive('/admin') ? 'var(--accent)' : 'var(--text-secondary)',
                    background: isActive('/admin') ? 'var(--accent-light)' : 'transparent'
                  }}>
                  Admin
                </Link>
                <Link
                  href="/account-dashboard"
                  onClick={() => setMenuOpen(false)}
                  className="px-3 py-2.5 rounded-lg text-sm font-medium"
                  style={{ color: 'var(--text-secondary)' }}>
                  Account Dashboard
                </Link>
                <Link
                  href="/account/emails"
                  onClick={() => setMenuOpen(false)}
                  className="px-3 py-2.5 rounded-lg text-sm font-medium"
                  style={{ color: 'var(--text-secondary)' }}>
                  Email History
                </Link>
                <Link
                  href="/settings"
                  onClick={() => setMenuOpen(false)}
                  className="px-3 py-2.5 rounded-lg text-sm font-medium"
                  style={{ color: 'var(--text-secondary)' }}>
                  Email Preferences
                </Link>
              </>
            )}
            {!effectiveUser && (
              <Link
                href="/sign-up-login"
                onClick={() => setMenuOpen(false)}
                className="px-3 py-2.5 rounded-lg text-sm font-medium btn-primary"
                style={{ color: '#fff', textAlign: 'center' }}>
                Sign In
              </Link>
            )}
          </div>
        </div>
      }
    </header>);

}